import { useEffect, useState } from "react";
import { Wheat, Plus, Trash2, Sprout, List, Map as MapIcon } from "lucide-react";
import { api, money, num, trDate, todayISO } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import RiceMap from "../components/farm/RiceMap";
import { HERO, HERO_TINT } from "../lib/heroes";

const emptyForm = {
  name: "",
  variety: "",
  area_donum: "",
  planted_date: "",
  harvested_date: "",
  yield_kg: "",
  sale_price_per_kg: "",
  expenses: "",
  note: "",
  path: [],
  center: null,
};

const RICE_VARIETIES = [
  "Osmancık-97", "Baldo", "Karacadağ", "Halilbey", "Gala",
  "Şumnu", "Ergene", "Efe", "Kızıltan", "Kale", "Neğiş",
];

export default function Rice() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const { form, set, setForm, reset } = useForm(emptyForm);

  const load = () => api.get("/rice").then(setItems).catch(() => setError("Kayıtlar yüklenemedi."));
  useEffect(() => {
    load();
    api.get("/settings").then(setSettings).catch(() => {});
  }, []);

  const totalDonum = items.reduce((a, r) => a + (Number(r.area_donum) || 0), 0);
  const totalYield = items.reduce((a, r) => a + (Number(r.yield_kg) || 0), 0);
  const totalRevenue = items.reduce((a, r) => a + (Number(r.yield_kg) || 0) * (Number(r.sale_price_per_kg) || 0), 0);
  const totalExpense = items.reduce((a, r) => a + (Number(r.expenses) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      variety: form.variety,
      area_donum: Number(form.area_donum) || 0,
      planted_date: form.planted_date || null,
      harvested_date: form.harvested_date || null,
      yield_kg: Number(form.yield_kg) || 0,
      sale_price_per_kg: Number(form.sale_price_per_kg) || 0,
      expenses: Number(form.expenses) || 0,
      note: form.note,
      path: form.path || [],
      center: form.center || null,
    };
    try {
      const rec = await api.post("/rice", payload);
      setItems((x) => [rec, ...x]);
      reset();
      setOpen(false);
      setError("");
    } catch {
      setError("Kayıt eklenemedi.");
    }
  };

  const remove = async (id) => {
    try {
      await api.del(`/rice/${id}`);
      setItems((x) => x.filter((i) => i.id !== id));
    } catch {
      setError("Silinemedi.");
    }
  };

  const openFromMap = ({ path, area_donum, center }) => {
    setForm({ ...emptyForm, path, area_donum: String(area_donum), center });
    setOpen(true);
  };

  return (
    <>
      <PageHeader
        eyebrow={`ÇELTİK ÜRETİMİ · ${settings?.total_donum || 700} DÖNÜM`}
        title="Çeltik tarlaları"
        subtitle="Tarla bazlı ekim, hasat, verim ve satış takibi. Haritadan poligon çizerek dönümü otomatik hesaplayın."
        heroImage={HERO.rice}
        heroTint={HERO_TINT.rice}
        actions={
          <button className="primary-btn" data-testid="rice-add-btn" onClick={() => { reset(); setOpen(true); }}>
            <Plus size={17} /> Tarla ekle
          </button>
        }
      />

      {error && <div className="api-error" data-testid="rice-error">{error}</div>}

      <div className="filter-row">
        <button className={`chip ${view === "list" ? "active" : ""}`} data-testid="rice-view-list" onClick={() => setView("list")}>
          <List size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> Liste
        </button>
        <button className={`chip ${view === "map" ? "active" : ""}`} data-testid="rice-view-map" onClick={() => setView("map")}>
          <MapIcon size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> Harita
        </button>
      </div>

      <section className="stats-mini-row">
        <StatMini label="Toplam tarla" value={`${items.length}`} tone="green" testId="rice-stat-count" />
        <StatMini label="Toplam alan" value={`${num(totalDonum)} dönüm`} tone="teal" testId="rice-stat-donum" />
        <StatMini label="Toplam verim" value={`${num(totalYield)} kg`} tone="orange" testId="rice-stat-yield" />
        <StatMini label="Ciro" value={money(totalRevenue)} tone="green" testId="rice-stat-revenue" />
        <StatMini label="Giderler" value={money(totalExpense)} tone="red" testId="rice-stat-expense" />
      </section>

      <div className="panel">
        {view === "map" ? (
          <RiceMap
            fields={items}
            center={settings?.map_center}
            onCreatePolygon={openFromMap}
            onSelectField={() => {}}
          />
        ) : items.length === 0 ? (
          <EmptyState icon={Sprout} title="Henüz tarla eklenmedi" text="700 dönümlük çeltik alanınızı tarla tarla ekleyerek verim ve maliyetleri takip edin." actionLabel="İlk tarlayı ekle" onAction={() => setOpen(true)} testId="rice-empty-add" />
        ) : (
          <div className="rice-list">
            {items.map((r) => {
              const revenue = (Number(r.yield_kg) || 0) * (Number(r.sale_price_per_kg) || 0);
              const profit = revenue - (Number(r.expenses) || 0);
              return (
                <div className="rice-card" key={r.id} data-testid={`rice-item-${r.id}`}>
                  <div className="rice-head">
                    <div className="rice-title">
                      <Wheat size={18} />
                      <div>
                        <b>{r.name}{r.variety ? <span className="rice-variety-badge">{r.variety}</span> : null}</b>
                        <small>{num(r.area_donum)} dönüm · Ekim {trDate(r.planted_date)} · Hasat {trDate(r.harvested_date)}</small>
                      </div>
                    </div>
                    <button className="delete-btn always" data-testid={`rice-delete-${r.id}`} onClick={() => remove(r.id)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="rice-stats">
                    <div><span>Verim</span><b>{num(r.yield_kg)} kg</b></div>
                    <div><span>Kg fiyatı</span><b>{money(r.sale_price_per_kg)}</b></div>
                    <div><span>Ciro</span><b>{money(revenue)}</b></div>
                    <div><span>Gider</span><b className="negative">{money(r.expenses)}</b></div>
                    <div><span>Net</span><b className={profit >= 0 ? "positive" : "negative"}>{money(profit)}</b></div>
                  </div>
                  {r.note && <p className="rice-note">{r.note}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <Modal title="Yeni çeltik tarlası" subtitle="ÇELTİK" onClose={() => setOpen(false)}>
          <form onSubmit={submit}>
            <FormField label="Tarla adı">
              <input required data-testid="rice-form-name" placeholder="Örn. Üst tarla" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </FormField>
            <FormField label="Çeltik cinsi">
              <input list="rice-varieties" data-testid="rice-form-variety" placeholder="Örn. Osmancık-97" value={form.variety} onChange={(e) => set("variety", e.target.value)} />
              <datalist id="rice-varieties">
                {RICE_VARIETIES.map((v) => <option key={v} value={v} />)}
              </datalist>
            </FormField>
            {form.path && form.path.length >= 3 && (
              <div className="map-drawn-badge" data-testid="rice-form-path-badge">
                <MapIcon size={13} /> Haritadan çizildi · {form.path.length} nokta · {num(Number(form.area_donum) || 0)} dönüm otomatik hesaplandı
              </div>
            )}
            <div className="form-row">
              <FormField label="Alan (dönüm)">
                <input required type="number" step="0.1" data-testid="rice-form-donum" value={form.area_donum} onChange={(e) => set("area_donum", e.target.value)} />
              </FormField>
              <FormField label="Verim (kg)">
                <input type="number" step="1" data-testid="rice-form-yield" value={form.yield_kg} onChange={(e) => set("yield_kg", e.target.value)} />
              </FormField>
            </div>
            <div className="form-row">
              <FormField label="Ekim tarihi">
                <input type="date" data-testid="rice-form-planted" value={form.planted_date} onChange={(e) => set("planted_date", e.target.value)} />
              </FormField>
              <FormField label="Hasat tarihi">
                <input type="date" data-testid="rice-form-harvested" value={form.harvested_date} onChange={(e) => set("harvested_date", e.target.value)} />
              </FormField>
            </div>
            <div className="form-row">
              <FormField label="Kg satış fiyatı (₺)">
                <input type="number" step="0.1" data-testid="rice-form-price" value={form.sale_price_per_kg} onChange={(e) => set("sale_price_per_kg", e.target.value)} />
              </FormField>
              <FormField label="Giderler (₺)">
                <input type="number" step="1" data-testid="rice-form-expenses" value={form.expenses} onChange={(e) => set("expenses", e.target.value)} />
              </FormField>
            </div>
            <FormField label="Not">
              <textarea data-testid="rice-form-note" placeholder="Gübre, ilaç, sulama vb." value={form.note} onChange={(e) => set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="rice-form-submit">Tarlayı kaydet</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
